import React, { useRef, useEffect, useState, useCallback } from "react";
import { useSocket } from "../../context/SocketProvider";
import { useLogin } from "../../context/LoginProvider";
import { useNavigate } from "react-router-dom";
import "./SignupAvatar.css";
import Navbar from "../../components/Navbar";
import AlertBox from "../../components/AlertBox/AlertBox";
// Import assets
import cupidImage from "../../assets/cupid.png";
import loadingGif from "../../assets/loadingGif.gif";

const AVATAR_MESSAGES = [
  "Hi! I'm Sol. Tell me about your interests!",
  "What activities do you enjoy?",
  "Tell me what makes you unique!",
  "I'm here to find your perfect match!",
];

const AvatarSignUp = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [interests, setInterests] = useState(""); // holds manual input or transcribed text
  const [isSubmitDisabled, setSubmitDisabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [avatarState, setAvatarState] = useState("idle"); // idle, listening, thinking, happy
  const [showTip, setShowTip] = useState(true);
  const [bgAnimation, setBgAnimation] = useState(false);
  const [avatarMessage, setAvatarMessage] = useState(AVATAR_MESSAGES[0]);
  const [transcriptionCount, setTranscriptionCount] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const { socket, audioSocket } = useSocket();
  const { userData, setUserData, isLoggedIn, guestData, setGuestUserData } =
    useLogin();
  const navigate = useNavigate();

  const audioContext = useRef(null);
  const scriptProcessor = useRef(null);
  const audioInput = useRef(null);
  const audioChunks = useRef([]);
  const transcriptionId = useRef("");
  const mediaStream = useRef();
  const sendInterval = useRef();
  const processingTimeoutRef = useRef(null);
  const speechSynthRef = useRef(null);
  const avatarRef = useRef(null);
  const textareaRef = useRef(null);
  const containerRef = useRef(null);

  // Speech synthesis setup
  useEffect(() => {
    // Check if speech synthesis is available
    if ('speechSynthesis' in window) {
      speechSynthRef.current = window.speechSynthesis;
    }
  }, []);

  // Text-to-speech function with same settings as guest info form
  const speakText = (text) => {
    if (speechSynthRef.current && 'speechSynthesis' in window) {
      // Cancel any ongoing speech
      speechSynthRef.current.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9; // Slightly slower for clarity
      utterance.pitch = 1.1; // Slightly higher pitch for friendliness
      utterance.volume = 0.7; // Moderate volume
      
      // Function to set voice with retry mechanism
      const setVoice = () => {
        const voices = speechSynthRef.current.getVoices();
        if (voices.length > 0) {
          // Try to find a female voice
          const femaleVoice = voices.find(voice => 
            voice.name.toLowerCase().includes('female') ||
            voice.name.toLowerCase().includes('zira') ||
            voice.name.toLowerCase().includes('susan') ||
            voice.name.toLowerCase().includes('samantha') ||
            voice.name.toLowerCase().includes('alex') ||
            voice.name.toLowerCase().includes('karen')
          );
          
          if (femaleVoice) {
            utterance.voice = femaleVoice;
          } else {
            // Fallback to any available voice
            utterance.voice = voices[0];
          }
        }
      };
      
      // Set voice immediately if available
      setVoice();
      
      // If no voices are loaded yet, wait for them
      if (speechSynthRef.current.getVoices().length === 0) {
        speechSynthRef.current.addEventListener('voiceschanged', setVoice, { once: true });
      }
      
      utterance.onstart = () => {
        setAvatarState("talking");
      };
      
      utterance.onend = () => {
        setAvatarState("idle");
      };
      
      utterance.onerror = (event) => {
        console.log('Speech synthesis error:', event.error);
        setAvatarState("idle");
      };
      
      try {
        speechSynthRef.current.speak(utterance);
      } catch (error) {
        console.log('Speech synthesis failed:', error);
        setAvatarState("idle");
      }
    }
  };

  // Handle window resize to ensure proper responsiveness
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Handle incoming transcription events from the server.
  // New transcription is appended to the current text.
  function handleTranscription({ data, tid }) {
    if (transcriptionId.current !== tid) return;
    console.log("live transcription arrived: " + data);

    if (isProcessing) {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
      setIsProcessing(false);
    }

    // Append new text to existing text with proper spacing
    setInterests((prevInterests) => {
      // Check if prevInterests exists and ends with a punctuation mark
      const punctuation = /[.,!?]$/;
      const needsPunctuation =
        prevInterests && !punctuation.test(prevInterests.trim());

      // Add appropriate spacing and punctuation
      if (!prevInterests) {
        return data.trim();
      } else if (needsPunctuation) {
        return `${prevInterests.trim()}. ${data.trim()}`;
      } else {
        return `${prevInterests.trim()} ${data.trim()}`;
      }
    });

    // Animate the textarea
    if (textareaRef.current) {
      textareaRef.current.classList.add("textarea-highlight");
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.classList.remove("textarea-highlight");
        }
      }, 500);
    }

    // Make avatar react to receiving transcription
    setAvatarState("thinking");

    // Change avatar message occasionally
    setTranscriptionCount((prev) => {
      const newCount = prev + 1;
      if (newCount % 3 === 0) {
        const randomIndex = Math.floor(Math.random() * AVATAR_MESSAGES.length);
        setAvatarMessage(AVATAR_MESSAGES[randomIndex]);
        setShowTip(true);
        setTimeout(() => setShowTip(false), 3000);
      }
      return newCount;
    });

    setTimeout(() => setAvatarState("listening"), 1500);
  }

  // Handle interest response from the server.
  const handleInterestResponse = useCallback(
    ({ interests: serverInterests }) => {
      if (serverInterests.length === 0) {
        setAlertMessage("No relevant interests found!");
        setShowAlert(true);
        setAvatarState("idle");
      } else if (isLoggedIn) {
        // Speak completion message before navigating
        speakText("Great! I found some perfect matches for you!");
        setAvatarState("happy");
        setShowTip(true);
        setAvatarMessage("Great! I found some perfect matches for you!");

        setTimeout(() => {
          setUserData((prev) => ({ ...prev, interests: serverInterests }));
          navigate("/profile-summary", { replace: true });
        }, 3000);
      } else {
        // Speak completion message before navigating
        speakText("Great! I found some perfect matches for you!");
        setAvatarState("happy");
        setShowTip(true);
        setAvatarMessage("Great! I found some perfect matches for you!");

        setTimeout(() => {
          setGuestUserData((prev) => ({
            ...prev,
            interests: serverInterests,
            isFreshGuest: false
          }));
          navigate("/profile-summary", { replace: true });
        }, 3000);
      }
      setSubmitDisabled(false);
    },
    [isLoggedIn, navigate, setUserData, setGuestUserData]
  );

  // Set up socket listeners.
  useEffect(() => {
    socket.on("transcription", handleTranscription);
    socket.on("interestResponse", handleInterestResponse);
    audioSocket.on("transcription", handleTranscription);
    audioSocket.on("interestResponse", handleInterestResponse);

    // Force any responsive layouts to update on mount
    if (containerRef.current) {
      containerRef.current.style.width = "100vw";
      containerRef.current.style.maxWidth = "100vw";
    }

    // Speak welcome message when component mounts
    setTimeout(() => {
      speakText("Hi! I'm Sol. Tell me about your interests!");
      setAvatarState("talking");
      setTimeout(() => {
        setAvatarState("idle");
        setShowTip(true);
      }, 3000);
    }, 800);

    // Add avatar animation on mount
    if (avatarRef.current) {
      avatarRef.current.classList.add("avatar-enter");
    }

    return () => {
      socket.off("transcription", handleTranscription);
      socket.off("interestResponse", handleInterestResponse);
      audioSocket.off("transcription", handleTranscription);
      audioSocket.off("interestResponse", handleInterestResponse);
    };
  }, [socket, audioSocket, handleInterestResponse]);

  // Handle textarea input
  const handleTextareaChange = (e) => {
    setInterests(e.target.value);

    // Make avatar react to typing occasionally
    if (Math.random() > 0.8 && !isRecording) {
      setAvatarState("thinking");
      setTimeout(() => setAvatarState("idle"), 800);
    }
  };

  // Send recorded audio to the server for transcription.
  const sendAudio = () => {
    if (audioChunks.current.length > 0) {
      const concatenatedBuffer = audioChunks.current.reduce((acc, buffer) => {
        const tmp = new Uint8Array(acc.byteLength + buffer.byteLength);
        tmp.set(new Uint8Array(acc), 0);
        tmp.set(new Uint8Array(buffer), acc.byteLength);
        return tmp.buffer;
      }, new ArrayBuffer(0));
      audioChunks.current = [];
      audioSocket.emit("TranscribeAudioMessage", {
        tid: transcriptionId.current,
        audio: concatenatedBuffer,
      });
    }
  };

  // Start recording: request microphone permission and start processing audio.
  const startRecording = async () => {
    const RandomNumber = Math.floor(100000 + Math.random() * 900000).toString();
    transcriptionId.current = Date.now() + RandomNumber;

    // Change avatar state and hide tip
    setAvatarState("listening");
    setShowTip(true);
    setAvatarMessage("I'm listening... Tell me about your interests!");
    setBgAnimation(true);

    // Animate the button
    if (avatarRef.current) {
      avatarRef.current.classList.add("avatar-bounce");
      setTimeout(() => {
        if (avatarRef.current) {
          avatarRef.current.classList.remove("avatar-bounce");
        }
      }, 1000);
    }

    try {
      mediaStream.current = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      audioContext.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      audioInput.current = audioContext.current.createMediaStreamSource(
        mediaStream.current
      );
      scriptProcessor.current = audioContext.current.createScriptProcessor(
        4096,
        1,
        1
      );

      scriptProcessor.current.onaudioprocess = (audioProcessingEvent) => {
        const inputBuffer = audioProcessingEvent.inputBuffer;
        const inputData = inputBuffer.getChannelData(0);
        const pcmData = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          pcmData[i] = Math.max(-1, Math.min(1, inputData[i])) * 0x7fff;
        }
        audioChunks.current.push(pcmData.buffer);
      };

      // Connect the processor to a zero-gain node so that the event fires without audible output.
      const zeroGain = audioContext.current.createGain();
      zeroGain.gain.value = 0;
      audioInput.current.connect(scriptProcessor.current);
      scriptProcessor.current.connect(zeroGain);
      zeroGain.connect(audioContext.current.destination);

      setIsRecording(true);
      setSubmitDisabled(true); // disable submit while recording
      setIsSubmitting(false);
      sendInterval.current = setInterval(sendAudio, 10000);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      setAvatarState("idle");
      setShowAlert(true);
      setAlertMessage(
        "Could not access microphone. Please check your permissions."
      );
      setBgAnimation(false);
    }
  };

  // Stop recording and send any remaining audio data.
  const stopRecording = () => {
    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach((track) => track.stop());
    }
    if (sendInterval.current) {
      clearInterval(sendInterval.current);
      sendInterval.current = null;
    }
    sendAudio(); // Send any remaining audio data

    if (scriptProcessor.current) scriptProcessor.current.disconnect();
    if (audioContext.current) audioContext.current.suspend();
    if (audioInput.current) audioInput.current.disconnect();
    setIsRecording(false);
    setBgAnimation(false);

    // Show processing indicator without replacing the text.
    setIsProcessing(true);
    setAvatarState("thinking");
    setShowTip(true);
    setAvatarMessage("Processing what you said...");

    processingTimeoutRef.current = setTimeout(() => {
      setIsProcessing(false);
      setSubmitDisabled(false);
      setAvatarState("idle");
      setShowTip(false);
    }, 2000);
  };

  // Handle submission by sending the interests (either typed or transcribed) to the server.
  const handleSubmit = async () => {
    if (!interests.trim()) {
      setAlertMessage("Please enter your interests.");
      setShowAlert(true);
      return;
    }
    setSubmitDisabled(true);
    setIsSubmitting(true);
    setAvatarState("thinking");
    setShowTip(true);
    setAvatarMessage("Finding your perfect matches...");

    // Animate the button click
    if (avatarRef.current) {
      avatarRef.current.classList.add("avatar-bounce");
      setTimeout(() => {
        if (avatarRef.current) {
          avatarRef.current.classList.remove("avatar-bounce");
        }
      }, 1000);
    }

    try {
      // First get interests from text
      const response = await fetch('https://soulmegle.com/api/auth/update-interests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: isLoggedIn ? userData.username : guestData.username,
          interests: [interests],
          isLoggedIn: isLoggedIn
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to update interests');
      }

      const data = await response.json();

      // Update local state
      if (isLoggedIn) {
        setUserData(prev => ({ ...prev, interests: data.interests }));
      } else {
        setGuestUserData(prev => ({ 
          ...prev, 
          interests: data.interests,
          isFreshGuest: false
        }));
      }

      // Always navigate to profile summary after updating interests
      navigate("/profile-summary", { replace: true });
    } catch (error) {
      console.error('Error updating interests:', error);
      setAlertMessage("Failed to update interests. Please try again.");
      setShowAlert(true);
      setSubmitDisabled(false);
      setIsSubmitting(false);
      setAvatarState("idle");
    }
  };

  const closeAlert = () => setShowAlert(false);

  // Get avatar class based on current state
  const getAvatarClass = () => {
    const baseClass = "avatar-image";
    switch (avatarState) {
      case "listening":
        return `${baseClass} avatar-listening`;
      case "thinking":
        return `${baseClass} avatar-thinking`;
      case "happy":
        return `${baseClass} avatar-happy`;
      case "talking":
        return `${baseClass} avatar-talking`;
      default:
        return baseClass;
    }
  };

  return (
    <div
      className={`cupid-container default-cupid-background ${bgAnimation ? "bg-pulse" : ""
        }`}
      ref={containerRef}
    >
      <Navbar />

      <div className="content-container with-avatar">
        <div className="avatar-container fade-up" ref={avatarRef}>
          <img src={cupidImage} alt="Sol" className={getAvatarClass()} />
          {showTip && <div className="avatar-tip fade-up">{avatarMessage}</div>}
          {isProcessing && (
            <div className="processing-indicator">
              <img src={loadingGif} alt="Processing" className="loading-gif" />
              <span>Processing...</span>
            </div>
          )}
        </div>

        <div className="right-content fade-up">
          <div className="greeting">
            Hello {isLoggedIn ? userData.name : "Guest User"}
          </div>
          <div className="instructions">
            Enter your interests below or click "Talk to Sol About Yourself" to
            record your voice. Sol will listen and help find your perfect
            matches!
            <br />
            "Please be specific and try to use one word for interest."
          </div>
          <div className="text-input-container">
            <textarea
              ref={textareaRef}
              className={`interests-textarea ${isRecording ? "recording-active" : ""
                }`}
              value={interests}
              onChange={handleTextareaChange}
              placeholder="Type your interests here..."
              disabled={isRecording || isSubmitting || isProcessing}
            />
          </div>
          <div className="button-group">
            {!isRecording ? (
              <button
                onClick={startRecording}
                disabled={isSubmitting || isProcessing}
                className="record-button pulse-animation"
              >
                <span className="button-icon">🎤</span> Talk to Sol About
                Yourself
              </button>
            ) : (
              <button
                onClick={stopRecording}
                disabled={isSubmitting || isProcessing}
                className="stop-button recording-pulse"
              >
                <span className="button-icon">⏹️</span> Stop Recording
              </button>
            )}
            <button
              onClick={handleSubmit}
              disabled={
                isSubmitDisabled ||
                isRecording ||
                isSubmitting ||
                isProcessing ||
                !interests.trim()
              }
              className="submit-button"
            >
              <span className="button-icon">✓</span> Submit
            </button>
          </div>
        </div>
      </div>
      {showAlert && (
        <AlertBox
          message={alertMessage}
          onClose={closeAlert}
          success="failure"
        />
      )}
    </div>
  );
};

export default AvatarSignUp;